import { expVal } from '@confluence/feature-experiments';
import {
	classifyContentPermissionError,
	isUnsupportedCustomerAccountFieldError,
} from '@confluence/content-unified-query/entry-points/errors.ts';
import { fg } from '@confluence/feature-gating';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

/** Analytics access is independent of page access, even when both share a query. */
export const getViewPageContentError = (
	error: ApolloErrorInterop | undefined,
	contentId: string,
	resolvedContentId: string | null | undefined,
): ApolloErrorInterop | undefined => {
	// Never use missing content or the previous page's data to dismiss a permission failure.
	if (!error || !contentId || resolvedContentId !== contentId) {
		return error;
	}

	const graphQLErrors = error.graphQLErrors.filter((graphQLError) => {
		if (fg('confluence_shared_content_permission_errors')) {
			if (
				classifyContentPermissionError(graphQLError, contentId, resolvedContentId) ===
				'optional-permission'
			) {
				markErrorAsHandled(graphQLError);
				return false;
			}
			return true;
		}
		if (
			(graphQLError.path?.[0] === 'contentAnalyticsViewers' && isUnauthorizedError(graphQLError)) ||
			(isUnsupportedCustomerAccountFieldError(graphQLError) &&
				expVal('confluence_exp_vp_document', 'isEnabled', false))
		) {
			markErrorAsHandled(graphQLError);
			return false;
		}
		return true;
	});

	if (graphQLErrors.length === error.graphQLErrors.length) {
		return error;
	}
	if (!graphQLErrors.length && !error.networkError) {
		markErrorAsHandled(error);
		return undefined;
	}

	return new ApolloErrorInterop({
		graphQLErrors,
		networkError: error.networkError,
		extraInfo: error.extraInfo,
	});
};
